import React from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
import PremiumLock from 'js/premium/PremiumLock';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { NavButtonLink } from './buttonStyles';
import { ChurnedUserReactivationDiscount } from './types';

const TRIGGER = 'reactivate_button';

const Text = styled.div`
  white-space: inherit;
  text-align: center;
`;

const NoWrapSpan = styled.span`
  white-space: nowrap;
`;

const MobileText = styled.div`
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MobileNavButtonContainer = styled.div`
  flex-shrink: 1;
  --buttons-small-buttons-height: 40px;
  --buttons-small-buttons-l-r-padding: var(--spacing-xs-8, 8px);
`;

const ButtonWrapper = styled.div`
  width: fit-content;
  min-width: 220px;
  max-width: 268px;
  flex: 0 0 auto;
`;

type ChurnedUserReactivationButtonProps = {
  discount: ChurnedUserReactivationDiscount;
  isMobileNav: boolean;
  /**
   * Reactivation autotest copy override (AutotestFocusedNavReactivation
   * `reactivateLabel` surface). null/undefined (control) keeps the offer-derived
   * text below unchanged.
   */
  reactivateLabel?: string | null;
};

export default function ChurnedUserReactivationButton({
  discount: { offer, value },
  isMobileNav,
  reactivateLabel,
}: ChurnedUserReactivationButtonProps) {
  const checkoutUrl = routes.checkout_path({
    feature: 'churned_user_reactivation',
    trigger: TRIGGER,
    from_navbar: true,
  });

  const offerButtonContent =
    offer === 'discount' ? (
      <Text>
        Reactivate Premium, <NoWrapSpan>{value} OFF</NoWrapSpan>
      </Text>
    ) : (
      <Text>
        Reactivate Premium <NoWrapSpan>for {value}</NoWrapSpan>
      </Text>
    );

  const offerMobileButtonContent =
    offer === 'discount' ? (
      <MobileText>Reactivate Premium, {value} OFF</MobileText>
    ) : (
      <MobileText>Reactivate Premium for {value}</MobileText>
    );

  // Autotest override: a variant may replace the offer-derived copy (control
  // leaves reactivateLabel null → unchanged). url/trigger stay tied to the offer.
  const buttonContent = reactivateLabel ? (
    <Text>{reactivateLabel}</Text>
  ) : (
    offerButtonContent
  );
  const mobileButtonContent = reactivateLabel ? (
    <MobileText>{reactivateLabel}</MobileText>
  ) : (
    offerMobileButtonContent
  );

  if (isMobileNav) {
    return (
      <ImpressionWatcher source={TRIGGER} extra={{ offer }}>
        <MobileNavButtonContainer>
          <TextButtonLink
            href={checkoutUrl}
            icon={<PremiumLock circleColor="gold" lockColor="white" />}
            trackClick={{ source: 'churned_user_reactivation_button' }}
            size="sm"
          >
            {mobileButtonContent}
          </TextButtonLink>
        </MobileNavButtonContainer>
      </ImpressionWatcher>
    );
  }

  return (
    <ButtonWrapper>
      <ImpressionWatcher source={TRIGGER} extra={{ offer }}>
        <NavButtonLink
          href={checkoutUrl}
          icon={<PremiumLock circleColor="white" lockColor="blue" />}
          trackClick={{ source: 'churned_user_reactivation_button' }}
        >
          {buttonContent}
        </NavButtonLink>
      </ImpressionWatcher>
    </ButtonWrapper>
  );
}
